import { rem } from "polished";
import styled from "styled-components";

import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import type { ImageFit } from "@/components/editor/shared/header/header-settings-component";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import StyledButtonContainer from "@/components/editor/themes/hazel/shared/cta-button";
import { ColorLabels } from "@/components/editor/utils/use-color";

export const PaddingWrapper = withViewportStyle("div")`
  padding: ${rem(64)} ${rem(24)} ${rem(24)} ${rem(24)};

  ${(props) => props.$viewport.tablet`
    padding: ${rem(24)};
  `}

  ${(props) => props.$viewport.mobile`
    padding: ${rem(16)};
  `}
`;

export const Container = withViewportStyle("div")`
  padding: 0 ${rem(84)} ${rem(60)};

  ${(props) => props.$viewport.tablet`
    padding: unset;
  `}
`;

export const ContentWrapper = withViewportStyle("div")<{
  isLogoShowing: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${rem(72)};
  
  .text-primitive {
    text-align: left;
  }

  .ck-content h1,
  .ck-content h2,
  .ck-content h3,
  .ck-content h4,
  .ck-content h5,
  .ck-content p {
    margin: 0
  }

  ${(props) => props.$viewport.tablet`
    grid-template-columns: 1fr;
    grid-gap: ${rem(48)};
    grid-template-areas: ${(props) =>
      `${
        props.isLogoShowing ? '"logo"' : ""
      } "heading" "ctaText" "subheading" "button"`};

    .text-primitive, 
    .ck-content h1,
    .ck-content h2,
    .ck-content h3,
    .ck-content h4,
    .ck-content h5,
    .ck-content p {
      text-align: center;
    }
  `}

  ${(props) => props.$viewport.mobile`
    grid-gap: ${rem(24)};
  `}
`;

export const LeftColumn = withViewportStyle("div")<{
  isLogoShowing: boolean;
}>`
  display: grid;
  grid-template-rows: min-content min-content;
  grid-row-gap: ${(props) => (props.isLogoShowing ? rem(32) : 0)};
  grid-template-areas:
    "logo"
    "heading";

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const RightColumn = withViewportStyle("div")`
  display: grid;
  grid-template-rows: min-content min-content;
  grid-row-gap: ${rem(24)};
  grid-template-areas:
    "ctaText"
    "subheading"
    "button";

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const LogoWrapper = withViewportStyle("div")`
  height: 100%;
  display: flex;
  align-items: center;

  ${(props) => props.$viewport.tablet`
    justify-content: center;
  `}
`;

export const Logo = withViewportStyle("img")<{ isLogoResized: boolean }>`
  max-height: ${({ isLogoResized }) => (isLogoResized ? rem(120) : rem(40))};
  max-width: 100%;
  object-fit: contain;

  ${(props) => props.$viewport.tablet`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(100) : rem(40))};
  `}

  ${(props) => props.$viewport.mobile`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(80) : rem(40))};
  `}
`;

export const HeadingWrapper = withViewportStyle("div")`
  grid-area: heading;

  .text-primitive.headingOne {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(24)};
  `}
`;

export const CTATextWrapper = withViewportStyle("div")`
  grid-area: ctaText;
  max-width: max-content;
  display: inline-flex;
  padding: ${rem(24)};
  border: ${rem(2)} solid ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};

  ${(props) => props.$viewport.tablet`
    max-width: none;
    display: block;
    padding: ${rem(12)};
    font-size: ${rem(18)};
    text-align: center;
  `}

  .text-primitive, 
  .hazel .ck-content h1,
  .hazel .ck-content h2,
  .hazel .ck-content h3,
  .hazel .ck-content h4,
  .hazel .ck-content h5,
  .hazel .ck-content p,
  .hazel .ck-content li {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]} !important;
  }
`;

export const SubheadingWrapper = styled.div`
  grid-area: subheading;

  .text-primitive.paragraph {
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }
`;

export const ButtonWrapper = withViewportStyle(StyledButtonContainer)`
  grid-area: button;

  ${(props) => props.$viewport.tablet`
    text-align: center;
    margin-bottom: ${rem(48)};
  `}

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(24)};
  `}
`;

export const ImageWrapper = withViewportStyle("div")`
  position: relative;
  height: ${rem(360)};

  ${(props) => props.$viewport.tablet`
    height: ${rem(314)};
  `}

  ${(props) => props.$viewport.mobile`
    height: ${rem(268)};
  `}
`;

export const LeftVectors = withViewportStyle("div")`
  position: absolute;
  top: ${rem(120)};
  left: ${rem(-12)};

  ${(props) => props.$viewport.tablet`
    top: ${rem(84)};
  `}

  ${(props) => props.$viewport.mobile`
    top: ${rem(48)};
    left: ${rem(-8)};
  `}

  .circle, .one-quarter-circle {
    path, circle {
      fill: ${({ theme }) => theme.colors[ColorLabels.VISUALS_3]};
    }
  }

  .one-quarter-circle {
    transform: scaleX(-1);
  }

  .circle {
    opacity: 0.6;
    margin-left: ${rem(-3)};
  }
`;

export const RightVectors = withViewportStyle("div")`
  position: absolute;
  bottom: ${rem(-24)};
  right: ${rem(-16)};

  ${(props) => props.$viewport.tablet`
    bottom: ${rem(-20)};
    right: ${rem(-12)};
  `}

  ${(props) => props.$viewport.mobile`
    bottom: ${rem(-16)};
    right: ${rem(-8)};
  `}

  .semicircle, .one-quarter-circle {
    path {
      fill: ${({ theme }) => theme.colors[ColorLabels.VISUALS_2]};
    }
  }

  .one-quarter-circle {
    opacity: 0.6;
    margin-right: ${rem(-3)};
  }
`;

export const Image = styled.img<{
  $position: ImagePosition;
  imageFit?: ImageFit;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ imageFit }) => imageFit || "cover"};
  object-position: ${(props) => props.$position};
  overflow: hidden;
`;
